import React, { useState, useEffect } from 'react';
import clsx from "clsx";
import ReactQuill from 'react-quill';
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Divider, Form, Input, Spin, Steps, Upload, message } from "antd";
import styled from "styled-components";
import axios from '../../utils/axios';
import Config from '../../config';
import { EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { getLocalStorageItem, uploadFile, getSecuredUrl } from "../../utils/utilities";
import { editOpportunity, getOpportunity, getProgramById, toggleSpinner, setEditOpportunitiesData } from "../../redux/actions";
import StyledText from "../../components/StyledText";
import Button from "../../components/Button";
import {
    opportunityDataSelector,
    toggleSpinnerSelector,
    editOpportunityData
} from "../../redux/selectors";
import {
    useAppDispatch,
    useAppSelector
} from "../../redux/store";
import 'react-quill/dist/quill.snow.css';

const { TextArea } = Input;

const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 14 },
};

const CreateDescription = ({ className }) => {
    const baseClassName = clsx("createProfile", className);
    const intl = useIntl();
    const [pictures, setPictures] = useState([]);
    const [docs, setDocs] = useState([]);
    const [mediaDescriptions, setMediaDescriptions] = useState([]);
    const [docsDescriptions, setDocsDescriptions] = useState([]);
    const [savedClicked, setSavedClicked] = useState(false);
    const opportunityData = useAppSelector(opportunityDataSelector) || {};
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const showSpinner = useAppSelector(toggleSpinnerSelector);
    const programReviewDetails = JSON.parse(getLocalStorageItem('programReviewDetails') || '{}');
    const { opportunityId, programID } = programReviewDetails || {};
    const { opportunity } = opportunityData || {};
    const [opportunitySummary, setOpportunitySummary] = useState('');
    const [opportunityObjectives, setOpportunityObjectives] = useState('');
    const maxWordsSummary = 30;
    const maxWordsObjectives = 300;
    const editOpp = useAppSelector(editOpportunityData) || {};
    const { opportunityID } = editOpp || {};
    const [quillError, setQuillError] = useState(false); // New state for Quill validation error

    useEffect(() => {
        if (opportunityID && !savedClicked) {
            dispatch(setEditOpportunitiesData(''));
            dispatch(toggleSpinner(false));
            navigate("/impact-maker/applications/create/location");
        }
    }, [opportunityID, dispatch, navigate, savedClicked]);

    useEffect(() => {
        if (opportunity?.opportunityName) {
            form.setFieldsValue({
                opportunitySummary: opportunity.opportunitySummary,
                opportunityObjectives: opportunity.opportunityObjectives,
            });
            if (opportunity?.media) {
                const fetchMediaUrls = async () => {
                    const updatedMedia = await Promise.all(
                        opportunity.media.map(async (pic) => {
                            if (pic.fileKey) {
                                try {
                                    const blob = await getSecuredUrl(pic.fileKey, true);
                                    const fullImageUrl = URL.createObjectURL(blob);
                                    return { ...pic, url: fullImageUrl };
                                } catch (error) {
                                    console.error(`Failed to fetch URL for ${pic.fileKey}:`, error);
                                    return pic;
                                }
                            }
                            return pic;
                        })
                    );
                    setPictures(updatedMedia);
                    const initialDescriptions = updatedMedia.reduce((acc, pic) => {
                        acc[pic.uid] = pic.mediaDescription || '';
                        return acc;
                    }, {});
                    setMediaDescriptions(initialDescriptions);
                };
                fetchMediaUrls();
            }
            if (opportunity?.uploadedDocuments) {
                setDocs(opportunity?.uploadedDocuments);
                const initialDescriptions = opportunity.uploadedDocuments.reduce((acc, pic) => {
                    acc[pic.uid] = pic.mediaDescription || '';
                    return acc;
                }, {});
                setDocsDescriptions(initialDescriptions);
            }
            setOpportunitySummary(opportunity.opportunitySummary || '');
            setOpportunityObjectives(opportunity.opportunityObjectives || '');
        }
    }, [opportunity?.opportunityName, form]);

    useEffect(() => {
        if (opportunityId) {
            dispatch(getOpportunity(opportunityId));
        }
    }, [opportunityId, programID, dispatch]);

    useEffect(() => {
        if (programID) {
            document.documentElement.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            dispatch(getProgramById(programID));
            dispatch(toggleSpinner(false));
        }
    }, [programID, dispatch]);

    const onFinishFailed = (errorInfo) => {
        console.log("Validation Failed:", errorInfo);
        // Check if opportunityObjectives is in error
        const hasQuillError = errorInfo.errorFields.some(
            (field) => field.name[0] === 'opportunityObjectives'
        );
        setQuillError(hasQuillError);
    };

    const handleProceed = () => {
        form.validateFields().then(() => {
            const formData = form.getFieldsValue();
            const data = {
                opportunitySummary: formData?.opportunitySummary,
                opportunityObjectives: formData?.opportunityObjectives,
                media: pictures?.map(({ uid, name, url, mediaDescription, fileKey, type }) => ({ uid, name, url, mediaDescription, fileKey, type })),
                uploadedDocuments: docs?.map(({ uid, name, url, mediaDescription, fileKey }) => ({ uid, name, url, mediaDescription, fileKey })),
                opportunityID: opportunityId,
                programID
            };
            dispatch(toggleSpinner(true));
            dispatch(editOpportunity(data));
            setQuillError(false); // Reset error state on successful validation
        }).catch((errorInfo) => {
            console.log('Validation Failed:', errorInfo);
            const hasQuillError = errorInfo.errorFields.some(
                (field) => field.name[0] === 'opportunityObjectives'
            );
            setQuillError(hasQuillError);
        });
    };

    const handleDescriptionChange = (key) => (e) => {
        const newDescriptions = { ...mediaDescriptions, [key]: e.target.value };
        setMediaDescriptions(newDescriptions);
        const updatedPictures = pictures.map((pic) =>
            pic.uid === key ? { ...pic, mediaDescription: e.target.value } : pic
        );
        setPictures(updatedPictures);
    };

    const handleDocsDescriptionChange = (key) => (e) => {
        const newDescriptions = { ...docsDescriptions, [key]: e.target.value };
        setDocsDescriptions(newDescriptions);
        const updatedDocs = docs.map((doc) =>
            doc.uid === key ? { ...doc, mediaDescription: e.target.value } : doc
        );
        setDocs(updatedDocs);
    };

    const handleSaveAndExit = () => {
        setSavedClicked(true);
        form.validateFields().then(() => {
            form.submit();
            localStorage.removeItem('programReviewDetails');
            navigate(`/impact-maker/applications/${programID}/${opportunityId}`);
            setQuillError(false); // Reset error state
        }).catch((errorInfo) => {
            console.log('Validation Failed:', errorInfo);
            dispatch(toggleSpinner(false));
            const hasQuillError = errorInfo.errorFields.some(
                (field) => field.name[0] === 'opportunityObjectives'
            );
            setQuillError(hasQuillError);
        });
    };

    const handlePrevious = () => {
        navigate("/impact-maker/applications/create/name");
    };

    const countWords = (text) => {
        const plainText = text?.replace(/<[^>]+>/g, '').trim();
        return plainText?.split(/\s+/)?.filter(word => word.length > 0).length || 0;
    };

    const handleTextChange = (setter, maxWords) => (e) => {
        const text = e.target.value;
        if (countWords(text) <= maxWords) {
            setter(text);
            form.setFieldsValue({ [e.target.name]: text });
        }
    };

    const handleQuillChange = (content, delta, source, editor) => {
        const plainText = editor.getText().trim();
        if (countWords(content) <= maxWordsObjectives) {
            setOpportunityObjectives(content);
            form.setFieldsValue({ opportunityObjectives: plainText.length > 0 ? content : '' });
            form.validateFields(['opportunityObjectives']);
        }
    };

    const onChange = ({ fileList: newFileList }) => {
        setPictures(prevPictures => {
            const updatedPictures = newFileList.map(newFile => {
                const existingPicture = prevPictures.find(pic => pic.uid === newFile.uid);
                return existingPicture ? { ...existingPicture, ...newFile } : newFile;
            });
            return updatedPictures;
        });
    };

    const onDocsChange = ({ fileList: newFileList }) => {
        setDocs(prevPictures => {
            const updatedPictures = newFileList.map(newFile => {
                const existingPicture = prevPictures.find(pic => pic.uid === newFile.uid);
                return existingPicture ? { ...existingPicture, ...newFile } : newFile;
            });
            return updatedPictures;
        });
    };

    const processFileUpload = async ({ onSuccess, onError, file, onProgress }) => {
        try {
            let presignedPostData = await getPresignedPostData(file);
            presignedPostData = await presignedPostData.data;
            await uploadFileToS3(presignedPostData, file, onProgress);
            onSuccess(null, file);
            setPictures(prevPictures =>
                prevPictures.map(pic =>
                    pic.uid === file.uid ? { ...pic, fileKey: presignedPostData.fields.key } : pic
                )
            );
        } catch (e) {
            message.error(`File upload failed. ${e.message || ''}`);
            onError(e);
        }
    };

    const processDocsUpload = async ({ onSuccess, onError, file, onProgress }) => {
        try {
            let presignedPostData = await getPresignedPostData(file);
            presignedPostData = await presignedPostData.data;
            await uploadFileToS3(presignedPostData, file, onProgress);
            onSuccess(null, file);
            setDocs(prevPictures =>
                prevPictures.map(pic =>
                    pic.uid === file.uid ? { ...pic, fileKey: presignedPostData.fields.key } : pic
                )
            );
        } catch (e) {
            message.error(`File upload failed. ${e.message || ''}`);
            onError(e);
        }
    };

    const getPresignedPostData = async (selectedFile) => new Promise((resolve, reject) => {
        const fileName = selectedFile.name.replace(/(?:\.(?![^.]+$)|[^\w.])+/g, '');
        const url = `${Config?.url.companyLogoUploadUrl}?fileName=${fileName}`;
        axios.get(url, (res, err) => {
            if (res && res.status === 200) {
                resolve(res.data);
            } else {
                reject(err.response);
            }
        });
    });

    const uploadFileToS3 = async (presignedPostData, file, onProgress) =>
        new Promise((resolve, reject) => {
            const formData = new FormData();
            const config = {
                headers: { "content-type": "multipart/form-data" },
            };
            Object.keys(presignedPostData.fields).forEach((key) => {
                formData.append(key, presignedPostData.fields[key]);
            });
            formData.append("file", file);
            uploadFile(
                presignedPostData.url,
                formData,
                (res, err) => {
                    if (res && res.status === 204) {
                        resolve(res.data);
                    } else {
                        reject(err.response);
                    }
                },
                config
            );
        });

    const onPreview = async (file) => {
        let src = file.url;
        if (!src && file.fileKey) {
            try {
                const blob = await getSecuredUrl(file.fileKey, false);
                src = URL.createObjectURL(blob);
            } catch (error) {
                console.error('Failed to fetch secured URL:', error);
                return;
            }
        }
        if (!src && file?.originFileObj instanceof Blob) {
            src = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file.originFileObj);
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => reject(new Error('Failed to read file'));
            });
        }
        if (src) {
            const image = new Image();
            image.src = src;
            const imgWindow = window.open(src);
            imgWindow?.document.write(image.outerHTML);
        } else {
            console.error('Failed to preview image: Source is invalid or unavailable.');
        }
    };

    const wordCountSummary = countWords(opportunitySummary);
    const wordCountObjectives = countWords(opportunityObjectives);

    const onDelete = (file) => {
        setPictures((prevPictures) => prevPictures.filter(picture => picture.uid !== file.uid));
    };

    const onRemove = async (file) => {
        const { deleteURL } = Config.url;
        const data = { bucketName: 'bg-uploaded-media', fileName: file.fileKey };
        axios.put(deleteURL, () => onDelete(file), data, true);
    };

    const onDocsDelete = (file) => {
        setDocs(prevDocs => prevDocs.filter(doc => doc.uid !== file.uid));
    };

    const onDocsRemove = async (file) => {
        const { deleteURL } = Config.url;
        const data = { bucketName: 'bg-uploaded-media', fileName: file.fileKey };
        axios.put(deleteURL, () => onDocsDelete(file), data, true);
    };

    const customItemRender = (originNode, file) => {
        if (file?.url && file?.type?.startsWith('image/')) {
            const containerStyle = {
                margin: '10px 0',
                position: 'relative',
            };

            const actionsStyle = {
                position: 'absolute',
                top: '50%',
                right: '25%',
                display: 'flex',
                gap: '5px',
            };

            const iconStyle = {
                color: '#fff',
                fontSize: '16px',
                cursor: 'pointer',
                background: 'rgba(0, 0, 0, 0.5)',
                padding: '5px',
                borderRadius: '4px',
            };
            return (
                <div style={containerStyle} className='Vineet-image-container'>
                    <img
                        src={file.url}
                        alt={file.name}
                        style={{ maxWidth: '100%', maxHeight: '85px', objectFit: 'contain' }}
                    />
                    <div style={actionsStyle}>
                        <EyeOutlined
                            style={iconStyle}
                            onClick={() => onPreview(file)}
                        />
                        <DeleteOutlined
                            style={iconStyle}
                            onClick={() => onRemove(file)}
                        />
                    </div>
                </div>
            );
        }
        return originNode;
    };

    return (
        <div className={baseClassName}>
            <Steps
                current={0}
                items={[
                    {
                        title: <StyledText as="p" variant="H3">{intl.formatMessage({ id: "step_1" })}</StyledText>,
                        description: <StyledText as="p" variant="B3c" className='mainTitle'>{intl.formatMessage({ id: "about_your_project" })}</StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">{intl.formatMessage({ id: "step_2" })}</StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>{intl.formatMessage({ id: "your_request" })}</StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">{intl.formatMessage({ id: "step_3" })}</StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>{intl.formatMessage({ id: "planned_impact_title" })}</StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">{intl.formatMessage({ id: "step_4" })}</StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>{intl.formatMessage({ id: "award_criteria" })}</StyledText>,
                    },
                ]}
            />
            {showSpinner && (
                <div className="overlay">
                    <Spin className="spinner" size="large" spinning={showSpinner} />
                </div>
            )}
            <StyledText as="p" variant="H1" className='mainTitle'>
                {intl.formatMessage({ id: "project_description" })}
            </StyledText>
            <div>
                <Form
                    {...formItemLayout}
                    labelAlign="left"
                    form={form}
                    onFinish={handleProceed}
                    onFinishFailed={onFinishFailed}
                    className="formContainer"
                    scrollToFirstError
                >
                    <StyledText as="p" variant="B3d">
                        {intl.formatMessage({ id: "please_share_a_summary_of_your_project" })}
                    </StyledText>
                    <Form.Item
                        name="opportunitySummary"
                        className="formItem"
                        rules={[{ whitespace: true, required: true, message: intl.formatMessage({ id: "enter" }) }]}
                    >
                        <div>
                            <TextArea
                                name="opportunitySummary"
                                value={opportunitySummary}
                                rows={2}
                                placeholder={intl.formatMessage({ id: "enter_project_summary" })}
                                onChange={handleTextChange(setOpportunitySummary, maxWordsSummary)}
                            />
                            <div className={`wordCount ${wordCountSummary >= maxWordsSummary ? 'error' : ''}`}>
                                {wordCountSummary}/{maxWordsSummary} words
                            </div>
                        </div>
                    </Form.Item>
                    <StyledText as="p" variant="B3d">
                        {intl.formatMessage({ id: "please_describe_your_project" })}
                    </StyledText>
                    <Form.Item
                        name="opportunityObjectives"
                        className="formItem"
                        rules={[
                            {
                                validator: (_, value) => {
                                    const text = value && typeof value === 'string' ? value.replace(/<[^>]+>/g, '').trim() : '';
                                    if (text.length > 0) {
                                        setQuillError(false);
                                        return Promise.resolve();
                                    }
                                    setQuillError(true);
                                    return Promise.reject(new Error(intl.formatMessage({ id: "please_enter" })));
                                }
                            }
                        ]}
                    >
                        <div>
                            <ReactQuill
                                theme="snow"
                                value={opportunityObjectives}
                                onChange={handleQuillChange}
                                placeholder={intl.formatMessage({ id: "for_example_what_do_you_want_to_do" })}
                                modules={{
                                    toolbar: [
                                        ['bold', 'italic', 'underline'],
                                        ['link'],
                                        [{ list: 'ordered' }, { list: 'bullet' }],
                                        ['clean'],
                                    ],
                                }}
                                className={quillError ? 'quill-error' : ''} // Apply error class conditionally
                            />
                            <div className={`wordCount ${wordCountObjectives >= maxWordsObjectives ? 'error' : ''}`}>
                                {wordCountObjectives}/{maxWordsObjectives} words
                            </div>
                        </div>
                    </Form.Item>
                    <StyledText as="span" variant="B3d">
                        {intl.formatMessage({ id: "additional_images" })}
                        <StyledText as="span" variant="B3a">{` (${intl.formatMessage({ id: "optional" })})`}</StyledText>
                    </StyledText>
                    <StyledText as="span" variant="B3">
                        {intl.formatMessage({ id: "bring_the_project_to_life_share_pictures_of_what_you_will_be_changing_or_plan_to_achieve" })}
                    </StyledText>
                    <Form.Item className='uploadWrapper'>
                        <Upload
                            listType="picture-card"
                            onChange={({ fileList, file, ...rest }) => {
                                const validFiles = fileList.filter(item =>
                                    ['image/png', 'image/jpg', 'image/jpeg', 'image/tiff'].includes(item.type)
                                );
                                if (file.status === 'error' && !validFiles.includes(file)) {
                                    message.error(intl.formatMessage({ id: "please_upload_this_file_type_in_the_add_files_section_below" }));
                                }
                                onChange({
                                    fileList: validFiles,
                                    ...rest
                                });
                            }}
                            fileList={pictures}
                            onPreview={onPreview}
                            onRemove={onRemove}
                            beforeUpload={(file) => {
                                const isValidType = ['image/png', 'image/jpg', 'image/jpeg', 'image/tiff'].includes(file.type);
                                if (!isValidType) {
                                    message.error(intl.formatMessage({ id: "please_upload_this_file_type_in_the_add_files_section_below" }));
                                }
                                return isValidType;
                            }}
                            customRequest={({ onSuccess, onError, file, onProgress }) => {
                                if (!['image/png', 'image/jpg', 'image/jpeg', 'image/tiff'].includes(file.type)) {
                                    message.error(intl.formatMessage({ id: "please_upload_this_file_type_in_the_add_files_section_below" }));
                                    onError(new Error('Invalid file type.'));
                                    return;
                                }
                                processFileUpload({
                                    onSuccess, onError, file, onProgress,
                                });
                            }}
                            itemRender={customItemRender}
                            multiple
                        >
                            {`+ ${intl.formatMessage({ id: "upload" })}`}
                        </Upload>
                        <div className='uploadInputWrapper'>
                            {pictures.map((file) => (
                                <Form.Item key={file.uid}>
                                    <Input
                                        placeholder={intl.formatMessage({ id: "enter_caption" })}
                                        value={mediaDescriptions[file.uid] || ''}
                                        onChange={handleDescriptionChange(file.uid)}
                                    />
                                </Form.Item>
                            ))}
                        </div>
                    </Form.Item>
                    <StyledText as="span" variant="B3d">
                        {intl.formatMessage({ id: "add_files" })}
                        <StyledText as="span" variant="B3a">{` (${intl.formatMessage({ id: "optional" })})`}</StyledText>
                    </StyledText>
                    <StyledText as="p" variant="B3">
                        {intl.formatMessage({ id: "upload_any_documents_that_you_wish_to_share" })}
                    </StyledText>
                    <Form.Item className='uploadWrapper'>
                        <Upload
                            listType="picture-card"
                            onChange={({ fileList, file, ...rest }) => {
                                const validFiles = fileList.filter(item =>
                                    ['application/msword', 'application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'video/mp4', 'video/quicktime', 'video/mpeg', 'video/x-msvideo', 'video/x-matroska'].includes(item.type)
                                );
                                if (file.status === 'error' && !validFiles.includes(file)) {
                                    message.error(intl.formatMessage({ id: "please_upload_images_in_the_additional_images_section_above" }));
                                }
                                onDocsChange({
                                    fileList: validFiles,
                                    ...rest
                                });
                            }}
                            fileList={docs}
                            onPreview={onPreview}
                            onRemove={onDocsRemove}
                            beforeUpload={(file) => {
                                const isValidType = ['application/msword', 'application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'video/mp4', 'video/quicktime', 'video/mpeg', 'video/x-msvideo', 'video/x-matroska'].includes(file.type);
                                if (!isValidType) {
                                    message.error(intl.formatMessage({ id: "please_upload_images_in_the_additional_images_section_above" }));
                                }
                                return isValidType;
                            }}
                            customRequest={({ onSuccess, onError, file, onProgress }) => {
                                if (!['application/msword', 'application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'video/mp4', 'video/quicktime', 'video/mpeg', 'video/x-msvideo', 'video/x-matroska'].includes(file.type)) {
                                    message.error(intl.formatMessage({ id: "please_upload_images_in_the_additional_images_section_above" }));
                                    onError(new Error('Invalid file type.'));
                                    return;
                                }
                                const isLessThan150MB = file.size / 1024 / 1024 < 150;
                                if (!isLessThan150MB) {
                                    message.error(intl.formatMessage({ id: 'this_file_exceeds_the_limit_of_150mb' }));
                                    onError(new Error('File size exceeds 150MB limit.'));
                                    return;
                                }
                                processDocsUpload({
                                    onSuccess, onError, file, onProgress,
                                });
                            }}
                            multiple
                        >
                            {`+ ${intl.formatMessage({ id: "upload" })}`}
                        </Upload>
                        <div className='uploadInputWrapper'>
                            {docs.map((file) => (
                                <Form.Item key={file.uid}>
                                    <Input
                                        placeholder={intl.formatMessage({ id: "enter_caption" })}
                                        value={docsDescriptions[file.uid] || ''}
                                        onChange={handleDocsDescriptionChange(file.uid)}
                                    />
                                </Form.Item>
                            ))}
                        </div>
                    </Form.Item>
                    <div className='buttonContainer'>
                        <Divider />
                        <div className='buttonContentContainer'>
                            <div className='nextAndPreviousContainer'>
                                <Button variant="secondary" type="button" onClick={handlePrevious}>
                                    {intl.formatMessage({ id: "previous" })}
                                </Button>
                                <Button variant="primary" htmlType="submit">
                                    {intl.formatMessage({ id: "next" })}
                                </Button>
                            </div>
                            <div className="saveAndExitButton">
                                <Button variant="secondary" htmlType="button" onClick={handleSaveAndExit}>
                                    {intl.formatMessage({ id: "save_n_exit" })}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Form>
            </div>
        </div>
    );
};

const StyledCreateDescription = styled(CreateDescription)`
&.createProfile {
    background: white;
    display: flex;
    padding: 20px;
    flex-direction: column;
    min-height: 60vh;
    margin-top: 50px;
    padding-bottom: 60px;
    overflow-y: auto;
    .uploadWrapper .ant-form-item-control-input-content {
        display: flex !important;
        width: 100%;
        .ant-upload-list {
            display: flex;
            flex-direction: column !important;
        }
        .uploadInputWrapper {
            gap: 56px;
            display: flex;
            flex-direction: column;
            margin-top: 30px;
            margin-left: 30px;
            width: 100%;
        }
    }
    .overlay {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 1000;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.45);
    }
    .spinner {
        z-index: 2000;
        margin: auto;
        left: 50%;
        right: 50%;
        position: absolute;
        bottom: 50%;
    }
    .mainTitle {
        margin-bottom: 20px;
    }
    .formContainer {
        display: flex;
        gap: 10px;
        flex-direction: column;
    }
    .wordCount {
        text-align: right;
        &.error {
            color: red;
        }
    }
    .formItem .ant-form-item-explain-error {
        margin-top: -20px !important;
    }
    .buttonContainer {
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        position: fixed;
        width: 100%;
        bottom: 0px;
        background: white;
        margin-left: -100px;
        .buttonContentContainer {
            display: flex;
            justify-content: center;
            width: 100%;
            position: relative;
            .saveAndExitButton {
                position: absolute;
                right: 0;
                margin-bottom: 15px;
            }
            .nextAndPreviousContainer {
                display: flex;
                gap: 20px;
                margin-bottom: 15px;
            }
        }
    }
    .quill-error {
        border: 1px solid red !important;
    }
}`;

export default StyledCreateDescription;